import _ from "lodash";
import {
  BuildingIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  GitBranchIcon,
  HashIcon,
  TableIcon,
  TextAlignStartIcon,
} from "lucide-react";

import {
  DocumentEnum,
  InvoiceStateEnum,
  PublicDocumentFragment,
  PublicDocumentLineItemsQuery,
} from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { DocumentPreview } from "@/components/organisms/DocumentPreview";
import { EnumColors } from "@/constants/values";
import { ObjectType } from "@/objects/@types";
import { iconsByTypename } from "@/objects/@icons";
import { useShallow } from "zustand/react/shallow";

type PublicDocumentLineItem = Extract<
  NonNullable<PublicDocumentLineItemsQuery["node"]>,
  { __typename?: "PublicDocumentObject" }
>["lineItems"][number];

/** The public page merges ``PUBLIC_DOCUMENT_LINE_ITEMS_QUERY`` onto the base node. */
type PublicDocumentWithLineItems = PublicDocumentFragment & {
  lineItems?: PublicDocumentLineItem[] | null;
};

const object: ObjectType<PublicDocumentFragment> = {
  humanName: "Public Document",
  inputKey: "document",
  typename: "PublicDocumentObject",
  ReactIcon: iconsByTypename.DocumentObject,
  isEntity: false,
  titleFieldKey: "name",
  getTitle: (data) => {
    return data?.name;
  },
  getEntityId: (data: any) => {
    return data?.entity?.id;
  },
  fieldDefinitions: [
    {
      objectKey: "id",
      linkedValuesKeys: ["id"],
      label: "Billing",
      type: "custom",
      ReactIcon: CreditCardIcon,
      asString: (data) => data?.id,
      getValue: () => (store) => {
        return (() => {
          const document = store(
            useShallow((state) => state.fieldValues),
          ) as PublicDocumentFragment;

          return (
            <div className="w-full p-8">
              <DocumentPreview document={document} />
            </div>
          );
        })();
      },
      getValueOptions: () => ({
        placeholder: "Billing info",
      }),
    },
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "Entity",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data: any) => data?.entity?.id,
      getValue: (data: any) => () => {
        return (
          <div className="w-full">
            <BoxForEntity
              identifier={data?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name,
      getValue: (data) => {
        return data?.name;
      },
      getValueOptions: () => ({
        placeholder: "Invoice 123, etc.",
      }),
    },
    {
      objectKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.description,
      getValue: (data) => {
        return data?.description;
      },
      getValueOptions: () => ({
        placeholder:
          "Invoice for 100 hours of project management services for the month of January 2025, etc.",
      }),
    },
    {
      objectKey: "reference",
      label: "Reference",
      type: "text",
      ReactIcon: HashIcon,
      asString: (data: any) => data?.reference,
      getValue: (data: any) => {
        return data?.reference;
      },
      getValueOptions: () => ({
        placeholder: "INV-123, etc.",
      }),
    },
    // {
    //   objectKey: "contact",
    //   inputKey: "contactIdentifier",
    //   label: "Contact",
    //   type: "reference",
    //   ReactIcon: IdCardIcon,
    //   asString: (data) => data?.contact?.emailAddress,
    //   getValue: (data) => ({
    //     name: data?.contact?.emailAddress,
    //     nodeId: data?.contact?.id,
    //   }),
    //   getValueOptions: (data) =>
    //     getReferenceValueOptionsByTypename("ContactObject")([
    //       data?.entity?.identifier,
    //     ]),
    //   createProps: {
    //     isRequired: true,
    //   },
    //   updateProps: {
    //     isRequired: true,
    //   },
    //   summaryProps: {
    //     isVisible: true,
    //   },
    //   fieldProps: {
    //     headerFieldType: "reference",
    //         //     isSortable: true,
    //     isFilterable: true,
    //   },
    // },
    {
      objectKey: "createdAt",
      label: "Date",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.createdAt,
      getValue: (data) => {
        return data?.createdAt;
      },
    },
    {
      objectKey: "documentType",
      inputKey: "type",
      label: "Type",
      type: "enum",
      ReactIcon: GitBranchIcon,
      asString: (data) => data?.documentType,
      getValue: (data) => data?.documentType,
      getValueOptions: () => ({
        enum: _.map(DocumentEnum, (value, key) => ({
          key,
          value,
        })),
        getColors: (mode) => ({
          [DocumentEnum.Invoice]: EnumColors.BLUE[mode],
          [DocumentEnum.PurchaseOrder]: EnumColors.AQUA[mode],
        }),
      }),
    },
    {
      objectKey: "invoiceState",
      inputKey: "state",
      label: "State",
      type: "enum",
      ReactIcon: GitBranchIcon,
      asString: () => "Unknown",
      getValue: (data) => data?.invoiceState,
      getValueOptions: () => ({
        enum: _.map(InvoiceStateEnum, (value, key) => ({
          key,
          value,
        })),
        getColors: (mode) => ({
          [InvoiceStateEnum.Paid]: EnumColors.GREEN[mode],
          [InvoiceStateEnum.Draft]: EnumColors.GREY_1[mode],
          [InvoiceStateEnum.Sent]: EnumColors.BLUE[mode],
        }),
      }),
      summaryProps: {
        isVisible: true,
      },
    },
    {
      objectKey: "lineItems",
      label: "Line Items",
      type: "table",
      ReactIcon: TableIcon,
      linkedValuesKeys: ["lineItems", "entity"],
      asString: () => "",
      getValue: (data) =>
        ((data as PublicDocumentWithLineItems)?.lineItems || []).map(
          (lineItem) => {
            if (lineItem.metadata?._frontend_uuid) {
              return lineItem;
            }
            if (lineItem.identifier) {
              return {
                ...lineItem,
                metadata: {
                  ...(lineItem.metadata ?? {}),
                  _frontend_uuid: lineItem.identifier,
                },
              };
            }
            return {
              ...lineItem,
              metadata: {
                ...(lineItem.metadata ?? {}),
                _frontend_uuid: crypto.randomUUID(),
              },
            };
          },
        ),
      getValueOptions: (data: any) => ({
        columns: [
          {
            label: "Description",
            type: "string",
            width: 100,
            headerFieldType: "string",
            objectKey: "description",
            isDisabled: false,
            isSearchable: false,
            isMultiline: true,
            getValue: (lineItemTransaction: PublicDocumentLineItem) =>
              lineItemTransaction?.description || "",
            getValueOptions: () => ({
              placeholder:
                "Project overview & design, stakeholder meetings, etc.",
            }),
          },
          {
            label: "Amount",
            type: "currency",
            width: 100,
            headerFieldType: "number",
            objectKey: "amount",
            isDisabled: false,
            isSearchable: false,
            getValue: (lineItemTransaction: PublicDocumentLineItem) =>
              lineItemTransaction?.amount,
            getValueOptions: () => ({
              placeholder: "$100.00",
            }),
            getValueForApi(lineItemTransaction: PublicDocumentLineItem) {
              return lineItemTransaction?.amount || 0;
            },
          },
          {
            label: "Quantity",
            type: "number",
            width: 100,
            headerFieldType: "number",
            objectKey: "quantity",
            isDisabled: false,
            isSearchable: false,
            getValue: (
              lineItemTransaction: PublicDocumentLineItem & {
                quantity?: number | null;
              },
            ) => lineItemTransaction?.quantity || 0,
            getValueForApi: (lineItemTransaction: any) =>
              lineItemTransaction?.quantity || 0,
            getValueOptions: () => ({
              placeholder: "100",
            }),
          },
        ],
      }),
    },
  ],
  getActions: (data) => {
    return [];
  },
};

export { object as PublicDocumentObject };
